import React, { useEffect } from 'react';
import { FieldArray, FormikProps } from 'formik';
import { Flex, Label, Box, Heading, Field, Card, Container, Text } from 'theme-ui';
import { GrayFormikField } from '@/components/GrayFormikField';
import { TrashIcon } from '@heroicons/react/outline';
import Button from '@/components/Button';
import { GetLicenseTypesResponse } from '@/types/types';
import CustomDatePicker from '@/components/CustomDatePicker';
import Error from '@/components/Error';
import { frequencyOptions } from './common';


interface LicenceTypeOption {
    value: number;
    label: string;
}
interface IncentiveFormMainProps extends FormikProps<any> {
    actionId: number;
    setActionId: (arg: number) => void;
    licenseTypesOptions: LicenceTypeOption[];
    additionalErrors?: React.ReactNode;
    addToFooter?: React.ReactNode;
}

export const getLicenseTypeOptions = (licenseTypes: GetLicenseTypesResponse) =>
    licenseTypes
        ? licenseTypes.Results.map((licenceType) => ({
            // @ts-ignore
            value: licenceType.Id,
            label: licenceType.Name,
        }))
        : [];

const getValidDateOrNull = (value?: string | null) => {
    if (!value) {
        return null;
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
};

export const validateIncentiveDates = (values: any) => {
    const errors: any = {};

    const { ValidFrom, ValidTo } = values || {};

    if (ValidFrom) {
        const fromDate = new Date(ValidFrom);
        if (isNaN(fromDate.getTime())) {
            errors.ValidFrom = 'Please provide a valid "Valid from" date';
        }
    }

    if (ValidTo) {
        const toDate = new Date(ValidTo);
        if (isNaN(toDate.getTime())) {
            errors.ValidTo = 'Please provide a valid "Valid to" date';
        }
    }

    if (!errors.ValidFrom && !errors.ValidTo && ValidFrom && ValidTo) {
        const fromDate = new Date(ValidFrom);
        const toDate = new Date(ValidTo);

        if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime()) && toDate < fromDate) {
            errors.ValidTo = '"Valid to" must be on or after "Valid from"';
        }
    }

    return errors;
};

export const IncentiveFormMain: React.FC<IncentiveFormMainProps> = (props) => {
    return (
        <>
            <GrayFormikField type="checkbox" label="Active" name="Active" />

            <GrayFormikField name="Heading" type="text" label="Heading" />

            <GrayFormikField
                name="_content"
                type="radio"
                label="Content"
                options={[
                    { value: 'file', label: 'File' },
                    { value: 'storage', label: 'Storage' },
                ]}
            />
            {props.values._content === 'storage' ? (
                <GrayFormikField name="StorageUrl" type="text" label="Storage URL" />
            ) : null}
            {props.values._content === 'file' ? (
                <Field
                    backgroundColor="gray.1"
                    type="file"
                    label="Upload HTML"
                    onChange={(e: any) => {
                        e.currentTarget.files[0].text().then((res) => {
                            props.setFieldValue('Body', res);
                            props.setFieldValue('StorageUrl', '');
                        });
                    }}
                />
            ) : null}

            <GrayFormikField name="SuccessButtonText" type="text" label="Success button text" />
            {/* <GrayFormikField name="Frequency" type="text" label="Frequency" /> */}
            {/* TODO: change it to dropdown */}
            <GrayFormikField
                label="Frequency"
                type="select"
                name="Frequency"
                options={frequencyOptions}
            />

            <GrayFormikField
                name="InitialDelayInSeconds"
                type="number"
                label="Initial delay in seconds after login"
            />
            <GrayFormikField
                name="InitialDeferredDays"
                type="number"
                label="Show after number of days from first login"
            />
            <GrayFormikField name="RecurrenceInterval" type="number" label="Recurrence interval" />
            <GrayFormikField
                name="MaxDisplayCount"
                type="number"
                label="Maximum number of appearances"
            />

            <Label>Valid from</Label>
            <CustomDatePicker
                variation="normal"
                field={{
                    name: 'ValidFrom',
                    value: getValidDateOrNull(props.values.ValidFrom),
                }}
                onChange={(value: Date | null) => {
                    props.setFieldValue('ValidFrom', value ? value.toISOString() : null);
                }}
            />
            {props.errors.ValidFrom && props.touched.ValidFrom ? (
                <Text color="danger">{props.errors.ValidFrom as string}</Text>
            ) : null}

            <Label>Valid to</Label>
            <CustomDatePicker
                variation="normal"
                field={{
                    name: 'ValidTo',
                    value: getValidDateOrNull(props.values.ValidTo),
                }}
                onChange={(value: Date | null) => {
                    props.setFieldValue('ValidTo', value ? value.toISOString() : null);
                }}
            />
            {props.errors.ValidTo && props.touched.ValidTo ? (
                <Text color="danger">{props.errors.ValidTo as string}</Text>
            ) : null}

            <Label>Company Ids</Label>
            <FieldArray
                name="CompanyIds"
                render={({ insert, remove, name }) => {
                    return (
                        <>
                            {props.values.CompanyIds.length === 0 ? (
                                <Button
                                    variant="secondary"
                                    type="button"
                                    onClick={() => insert(0, '')}
                                >
                                    Add company Id
                                </Button>
                            ) : (
                                props.values.CompanyIds.map((companyId, index) => {
                                    return (
                                        <>
                                            <Flex
                                                sx={{
                                                    alignItems: 'center',
                                                    gap: 2,
                                                }}
                                                key={index}
                                            >
                                                <Box
                                                    sx={{
                                                        flexGrow: 1,
                                                    }}
                                                >
                                                    <GrayFormikField name={`CompanyIds.${index}`} />
                                                </Box>
                                                <Button
                                                    variant="danger"
                                                    type="button"
                                                    onClick={() => remove(index)} // remove a friend from the list
                                                >
                                                    <TrashIcon width={16} height={16} />
                                                </Button>
                                            </Flex>
                                            {props.values.CompanyIds.length === index + 1 ? (
                                                <Button
                                                    variant="secondary"
                                                    type="button"
                                                    onClick={() => insert(index + 1, '')}
                                                >
                                                    Add company Id
                                                </Button>
                                            ) : null}
                                        </>
                                    );
                                })
                            )}
                        </>
                    );
                }}
            />
        </>
    );
};

interface CriteriaFieldProps extends FormikProps<any> {
    licenceTypeOptions: LicenceTypeOption[];
}
const CriteriaField: React.FC<CriteriaFieldProps> = ({
    licenceTypeOptions,
    setFieldValue,
    values,
    initialValues,
}) => {
    useEffect(() => {
        licenceTypeOptions.forEach((option, index) => {
            setFieldValue(`Criteria.${index}.checked`, false);
            setFieldValue(`Criteria.${index}.Value`, option.value);
            setFieldValue(`Criteria.${index}.CriteriaType`, 'LicenseAvailability');
            setFieldValue(`Criteria.${index}.InvertCondition`, false);
        });

        values?.Criteria?.forEach((criterium) => {
            const matchedLicenseIndex = licenceTypeOptions.findIndex(
                (l) => l.value === Number(criterium.Value)
            );

            if (criterium.CriteriaType === 'LicenseAvailability' && matchedLicenseIndex !== -1) {
                setFieldValue(`Criteria.${matchedLicenseIndex}.checked`, true);
                setFieldValue(
                    `Criteria.${matchedLicenseIndex}.InvertCondition`,
                    criterium.InvertCondition
                );
            }
        });

        return () => {
            licenceTypeOptions.forEach((option, index) => {
                setFieldValue(`Criteria.${index}.checked`, undefined);
                setFieldValue(`Criteria.${index}.Value`, undefined);
                setFieldValue(`Criteria.${index}.CriteriaType`, undefined);
                setFieldValue(`Criteria.${index}.InvertCondition`, undefined);
            });
        };
    }, [
        licenceTypeOptions.map((o) => o.value).join(','),
        initialValues?.Criteria?.map((o) => o.value).join(','),
    ]);

    return (
        <>
            {licenceTypeOptions.map((licenseType, index) => {
                return (
                    <Flex
                        sx={{
                            alignItems: 'center',
                            gap: 2,
                        }}
                        key={`${licenseType.value}-${index}`}
                    >
                        <GrayFormikField
                            type="checkbox"
                            label={licenseType.label}
                            name={`Criteria.${index}.checked`}
                        />

                        <GrayFormikField
                            type="checkbox"
                            label="Inverted"
                            name={`Criteria.${index}.InvertCondition`}
                        />
                    </Flex>
                );
            })}
        </>
    );
};

interface IncentiveFormCriteriaProps extends FormikProps<any> {
    licenseTypesOptions: LicenceTypeOption[];
}
export const IncentiveFormCriteria: React.FC<IncentiveFormCriteriaProps> = ({
    licenseTypesOptions,
    ...props
}) => {
    return (
        <>
            <Heading as="h3">Criteria</Heading>
            <CriteriaField licenceTypeOptions={licenseTypesOptions} {...props} />
        </>
    );
};

interface IncentiveFormActionProps {
    actions: any[];
}
export const IncentiveFormAction: React.FC<IncentiveFormActionProps> = ({ actions }) => {
    return (
        <>
            <Heading as="h3">Action</Heading>

            <GrayFormikField
                label="Action"
                type="select"
                name="ActionId"
                options={actions.map((action) => ({
                    value: action.Id,
                    label: `${action.Id} - ${action.ActionType} ${action.Description ? `- ${action.Description}` : ''
                        }`,
                }))}
            />
        </>
    );
};

const IncentiveForm: React.FC<
    IncentiveFormMainProps & IncentiveFormCriteriaProps & IncentiveFormActionProps
> = ({
    actionId,
    setActionId,
    licenseTypesOptions,
    additionalErrors,
    addToFooter,
    ...formProps
}) => (
        <Flex backgroundColor="Background" sx={{ flexDirection: 'column', borderRadius: 'sm' }}>
            <Flex
                p="2"
                sx={{
                    gap: 2,
                    '@media only screen and (max-width:60em)': {
                        flexDirection: 'column',
                    },
                    borderRadius: 'sm',
                }}
            >
                <Box
                    sx={{
                        flexBasis: '50%',
                        borderRadius: 'sm',
                        borderStyle: 'solid',
                        borderWidth: '1px',
                        borderColor: 'gray.2',
                        alignSelf: 'flex-start',
                    }}
                    p="3"
                >
                    <IncentiveFormMain
                        actionId={actionId}
                        setActionId={setActionId}
                        licenseTypesOptions={licenseTypesOptions}
                        {...formProps}
                    />
                </Box>

                <Flex
                    sx={{
                        gap: 2,
                        flexBasis: '50%',
                        alignSelf: 'flex-start',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        p="3"
                        sx={{
                            borderRadius: 'sm',
                            borderStyle: 'solid',
                            borderWidth: '1px',
                            borderColor: 'gray.2',
                        }}
                    >
                        <IncentiveFormCriteria
                            licenseTypesOptions={licenseTypesOptions}
                            {...formProps}
                        />
                    </Box>
                    <Box
                        p="3"
                        sx={{
                            borderRadius: 'sm',
                            borderStyle: 'solid',
                            borderWidth: '1px',
                            borderColor: 'gray.2',
                        }}
                    >
                        <IncentiveFormAction {...formProps} />
                    </Box>
                </Flex>
            </Flex>
            {additionalErrors ? (
                <Box p="2">
                    <Container variant="error-message">
                        <Error error={additionalErrors as any} />
                    </Container>
                </Box>
            ) : null}
            <Flex
                p="2"
                sx={{
                    width: '100%',
                    gap: 2,
                    justifyContent: 'flex-end',
                }}
            >
                {addToFooter}
                <Button
                    isLoading={formProps.isSubmitting}
                    disabled={formProps.isSubmitting}
                    type="submit"
                    onClick={formProps.submitForm}
                >
                    Save
                </Button>
            </Flex>
        </Flex>
    );

export default IncentiveForm;
